import React from 'react';

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  CADENCES,
  COMPARISONS,
  type Cadence,
  type Comparison,
  FILTER_DEFINITIONS,
  type FilterKey,
  type LebtFilters,
  METRICS,
  type Metric,
  SPLITS,
  type Split,
  comparisonWindow,
  defaultFilters,
} from './model';

type Props = {
  filters: LebtFilters;
  options: Record<string, Array<string>>;
  onChange: (change: Partial<LebtFilters>) => void;
};

export default function LebtCommercialFilters({ filters, options, onChange }: Props) {
  const saved = defaultFilters();
  const comparison = comparisonWindow(filters);
  const select = (key: FilterKey, values: Array<string>) =>
    onChange({ selections: { ...filters.selections, [key]: values } });
  const date = (label: string, value: string, change: (value: string) => void) => (
    <TextField
      label={label}
      type="date"
      size="small"
      value={value}
      onChange={(event) => event.target.value && change(event.target.value)}
      InputLabelProps={{ shrink: true }}
      inputProps={{ max: saved.to }}
    />
  );
  const choice = <T extends string>(
    label: string,
    value: T,
    values: ReadonlyArray<{ value: T; label: string }>,
    change: (value: T) => void,
  ) => (
    <TextField
      select
      label={label}
      size="small"
      value={value}
      onChange={(event) => change(event.target.value as T)}
      sx={{ minWidth: 160 }}
    >
      {values.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </TextField>
  );
  const plain = <T extends string>(values: ReadonlyArray<T>) => values.map((value) => ({ value, label: value }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={2}>
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          {date('P. Start Date', filters.from, (from) => onChange({ from }))}
          {date('P. End Date', filters.to, (to) => onChange({ to }))}
          {choice<Comparison>('P. Comparison Logic', filters.comparison, COMPARISONS, (value) =>
            onChange({ comparison: value }),
          )}
          {filters.comparison === 'Custom' && (
            <>
              {date('P. Comp Start Date', filters.compareFrom, (compareFrom) => onChange({ compareFrom }))}
              {date('P. Comp End Date', filters.compareTo, (compareTo) => onChange({ compareTo }))}
            </>
          )}
          <Typography variant="caption" color="text.secondary">
            Compared with {comparison.from} to {comparison.to}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          {choice<Cadence>('P. Date Cadence', filters.cadence, plain(CADENCES), (cadence) => onChange({ cadence }))}
          {choice<Metric>('Metric', filters.metric, plain(METRICS), (metric) => onChange({ metric }))}
          {choice<Split>('P. Data Split', filters.split, plain(SPLITS), (split) => onChange({ split }))}
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {FILTER_DEFINITIONS.map(({ key, label }) => {
            const selected = filters.selections[key];
            const choices = [...new Set([...(options[key] ?? []), ...selected])];
            return (
              <Autocomplete
                key={key}
                multiple
                disableCloseOnSelect
                size="small"
                limitTags={1}
                options={choices}
                value={selected}
                onChange={(_, values) => select(key, values.map(String))}
                renderTags={(values, getTagProps) =>
                  values.map((option, index) => {
                    const { key: tagKey, ...tagProps } = getTagProps({ index });
                    return <Chip key={tagKey} {...tagProps} label={option} size="small" />;
                  })
                }
                renderOption={(props, option, { selected: checked }) => (
                  <Box component="li" {...props} key={option}>
                    <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
                    {option}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={label} placeholder={selected.length ? '' : 'All'} />
                )}
              />
            );
          })}
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Button size="small" onClick={() => onChange({ ...saved, tab: filters.tab })}>
            Reset to saved view
          </Button>
          <Typography variant="caption" color="text.secondary">
            Product starts on the workbook&apos;s saved selection, which leaves out gift cards, purchased credit and
            items with no matched sale. Clear it to include them.
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}
